/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Interactive Story Component
 *
 * Full interactive decision pack with three layers:
 * - Layer 1: Narrative spine (5 screens)
 * - Layer 2: System chapters (accessible from options screen)
 * - Layer 3: Technical appendices (accessible from various screens)
 *
 * Overlays open inline without navigating away from /story/{code}
 */

import { useState } from 'react';
import { StoryProvider, useStoryContext } from '../context/StoryContext';
import Screen1YourHome from '../pages/interactive-story-screens/Screen1YourHome';
import Screen2WhatStaysSame from '../pages/interactive-story-screens/Screen2WhatStaysSame';
import Screen3WhatChanges from '../pages/interactive-story-screens/Screen3WhatChanges';
import Screen4TwoOptions from '../pages/interactive-story-screens/Screen4TwoOptions';
import Screen5ComfortControls from '../pages/interactive-story-screens/Screen5ComfortControls';
import SystemChapter from './SystemChapter';
import TechnicalAppendix from './TechnicalAppendix';

const screens = [
  { id: 1, component: Screen1YourHome, title: 'Your Home' },
  { id: 2, component: Screen2WhatStaysSame, title: 'What Stays' },
  { id: 3, component: Screen3WhatChanges, title: 'What Changes' },
  { id: 4, component: Screen4TwoOptions, title: 'Two Options' },
  { id: 5, component: Screen5ComfortControls, title: 'Comfort' },
];

interface InteractiveStoryProps {
  documentCode: string;
}

/**
 * Inner story component that uses the context
 */
function InteractiveStoryContent({ documentCode }: InteractiveStoryProps) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'error'>('idle');
  const { overlay, isOverlayOpen, closeOverlay } = useStoryContext();

  const goToScreen = (index: number) => {
    if (index >= 0 && index < screens.length) {
      setCurrentScreen(index);
    }
  };

  const nextScreen = () => goToScreen(currentScreen + 1);
  const prevScreen = () => goToScreen(currentScreen - 1);

  const handleShareLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShareState('copied');
        setTimeout(() => setShareState('idle'), 2000);
      } catch {
        setShareState('error');
        setTimeout(() => setShareState('idle'), 2000);
      }
      document.body.removeChild(textArea);
    }
  };

  const CurrentComponent = screens[currentScreen].component;

  return (
    <div className={`interactive-story ${isOverlayOpen ? 'overlay-open' : ''}`}>
      {/* Header */}
      <div className="interactive-story-header">
        <div className="header-info">
          <h1 className="header-title">Your Heating Recommendation</h1>
          <p className="header-progress">Explanation {currentScreen + 1} of {screens.length}</p>
        </div>
        <button
          className={`share-link-button ${shareState}`}
          onClick={handleShareLink}
          aria-label="Copy link to share"
        >
          {shareState === 'copied' ? (
            <>
              <span className="share-icon">✓</span>
              <span className="share-text">Link copied</span>
            </>
          ) : shareState === 'error' ? (
            <>
              <span className="share-icon">!</span>
              <span className="share-text">Copy failed</span>
            </>
          ) : (
            <>
              <span className="share-icon">🔗</span>
              <span className="share-text">Share link</span>
            </>
          )}
        </button>
      </div>

      {/* Content Area with fade transition */}
      <div className="interactive-story-content">
        <div className="screen-wrapper" key={currentScreen}>
          <CurrentComponent />
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="interactive-story-footer">
        <button
          className="nav-arrow nav-prev"
          onClick={prevScreen}
          disabled={currentScreen === 0}
          aria-label="Previous screen"
        >
          ◀︎
        </button>

        <div className="nav-dots-container">
          <div className="nav-dots">
            {screens.map((screen, index) => (
              <button
                key={screen.id}
                className={`nav-dot ${index === currentScreen ? 'active' : ''}`}
                onClick={() => goToScreen(index)}
                aria-label={`Go to ${screen.title}`}
              />
            ))}
          </div>
          <div className="nav-labels">
            {screens.map((screen, index) => (
              <button
                key={screen.id}
                className={`nav-label ${index === currentScreen ? 'active' : ''}`}
                onClick={() => goToScreen(index)}
                aria-label={`Go to ${screen.title}`}
              >
                {screen.title}
              </button>
            ))}
          </div>
        </div>

        <button
          className="nav-arrow nav-next"
          onClick={nextScreen}
          disabled={currentScreen === screens.length - 1}
          aria-label="Next screen"
        >
          ▶︎
        </button>
      </div>

      {/* Layer 2: System Chapter Overlay */}
      {overlay.type === 'system-chapter' && overlay.id && (
        <SystemChapter optionId={overlay.id} />
      )}

      {/* Layer 3: Technical Appendix Overlay */}
      {overlay.type === 'technical-appendix' && overlay.id && (
        <TechnicalAppendix appendixId={overlay.id} onClose={closeOverlay} />
      )}
    </div>
  );
}

/**
 * Main Interactive Story component wrapped with StoryProvider
 */
function InteractiveStory({ documentCode }: InteractiveStoryProps) {
  return (
    <StoryProvider>
      <InteractiveStoryContent documentCode={documentCode} />
    </StoryProvider>
  );
}

export default InteractiveStory;
